import type { Extent } from 'ol/extent';
import type Geometry from 'ol/geom/Geometry';
import RBush from 'ol/structs/RBush';
import type { AppState, AttributeValue, LayerModel, OriginalAttributes, VectorFeatureRecord, VectorLayerModel, WorkScope } from '../core/types';
import { normalizeWorkScope, workScopeFragmentIds } from './scope';
import { filteredMasterFragmentIds } from './attributeFilters';

const normalizeField = (value: string): string => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '').toLocaleLowerCase();
const aliases = {
  finca: ['codfinca', 'codigofinca'],
  suerte: ['codsuerte', 'codigosuerte', 'suerte'],
  tablon: ['tablon', 'codtablon'],
};

const attribute = (attributes: OriginalAttributes, names: readonly string[]): AttributeValue | undefined => {
  const wanted = new Set(names.map(normalizeField));
  const key = Object.keys(attributes).find((candidate) => wanted.has(normalizeField(candidate)));
  return key ? attributes[key] : undefined;
};
const text = (value: AttributeValue | undefined): string => value === null || value === undefined ? '' : String(value).trim();

export function layerHasCasurScopeKeys(layer: VectorLayerModel): boolean {
  const fields = new Set(layer.attributeFields.map(normalizeField));
  return Object.values(aliases).every((names) => names.some((name) => fields.has(name)));
}

export function recordLogicalIds(attributes: OriginalAttributes): { fincaId: string; suerteId: string; tablonId?: string } | null {
  const finca = text(attribute(attributes, aliases.finca));
  const suerte = text(attribute(attributes, ['codsuerte', 'codigosuerte'])) || text(attribute(attributes, ['suerte']));
  const tablon = text(attribute(attributes, aliases.tablon));
  if (!finca || !suerte) return null;
  const fincaId = `finca:${finca}`;
  const suerteId = `${fincaId}/suerte:${suerte}`;
  return { fincaId, suerteId, ...(tablon ? { tablonId: `${suerteId}/tablon:${tablon}` } : {}) };
}

/** Master fragment geometries indexed by extent, built once per Maestro model for the "Todo CASUR" spatial scope. */
const masterIndexCache = new WeakMap<LayerModel, RBush<Geometry>>();
const masterGeometryIndex = (master: LayerModel): RBush<Geometry> => {
  const cached = masterIndexCache.get(master); if (cached) return cached;
  const geometries = master.fragments.flatMap(({ feature }) => { const geometry = feature.getGeometry(); return geometry ? [geometry] : []; });
  const index = new RBush<Geometry>(); index.load(geometries.map((geometry) => geometry.getExtent()), geometries);
  masterIndexCache.set(master, index);
  return index;
};

/** Same records + Maestro + mode + scope → same Set: DisplaySet recomputations (e.g. a new subarea) reuse unchanged layers. */
const layerScopeCache = new WeakMap<readonly VectorFeatureRecord[], { readonly master: LayerModel; readonly mode: VectorLayerModel['scopeMode']; readonly key: string; readonly ids: ReadonlySet<string> }>();

/**
 * Records of a dependent layer inside the WorkScope.
 * An empty WorkScope is the compact form of "every finca of the Maestro" (Todo CASUR, see workScopeFragmentIds):
 * it resolves exactly like the fully expanded scope, never as "nothing".
 * - attributes: records whose CASUR keys resolve to a finca/suerte/tablón of the scope (Todo CASUR → any Maestro finca);
 * - spatial: records whose extent meets a fragment of the scope (Todo CASUR → any Maestro fragment).
 * Attribute filters are not part of the WorkScope and never apply here.
 */
export function featureIdsForLayerScope(layer: VectorLayerModel, master: LayerModel | null, requested: WorkScope, mode = layer.scopeMode): ReadonlySet<string> {
  if (layer.kind === 'working' || mode === 'none' || !master) return new Set(layer.features.map(({ id }) => id));
  const scope = normalizeWorkScope(master, requested);
  const key = `${scope.fincaIds.join(',')}|${scope.suerteIds.join(',')}|${scope.tablonIds.join(',')}`;
  const cached = layerScopeCache.get(layer.features);
  if (cached && cached.master === master && cached.mode === mode && cached.key === key) return cached.ids;
  const ids = mode === 'attributes' ? attributeScopedIds(layer, master, scope) : spatialScopedIds(layer, master, scope);
  layerScopeCache.set(layer.features, { master, mode, key, ids });
  return ids;
}

function attributeScopedIds(layer: VectorLayerModel, master: LayerModel, scope: WorkScope): ReadonlySet<string> {
  const allCasur = !scope.fincaIds.length;
  const fincaIds = new Set(scope.fincaIds); const suerteIds = new Set(scope.suerteIds); const tablonIds = new Set(scope.tablonIds);
  return new Set(layer.features.filter((record) => {
    const ids = recordLogicalIds(record.attributes);
    if (!ids) return false;
    if (allCasur) return master.fincas.has(ids.fincaId);
    if (tablonIds.size) return ids.tablonId ? tablonIds.has(ids.tablonId) : false;
    if (suerteIds.size) return suerteIds.has(ids.suerteId);
    return fincaIds.has(ids.fincaId);
  }).map(({ id }) => id));
}

function spatialScopedIds(layer: VectorLayerModel, master: LayerModel, scope: WorkScope): ReadonlySet<string> {
  const touches = (geometry: Geometry, extent: Extent, candidate: Geometry): boolean => candidate.intersectsExtent(extent) && geometry.intersectsExtent(candidate.getExtent());
  if (!scope.fincaIds.length) {
    const index = masterGeometryIndex(master);
    return new Set(layer.features.filter((record) => {
      const geometry = record.feature.getGeometry(); if (!geometry) return false;
      const extent = geometry.getExtent();
      return index.getInExtent(extent).some((candidate) => touches(geometry, extent, candidate));
    }).map(({ id }) => id));
  }
  const masterGeometries = [...workScopeFragmentIds(master, scope)].flatMap((id) => {
    const geometry = master.fragmentsById.get(id)?.feature.getGeometry();
    return geometry ? [geometry] : [];
  });
  return new Set(layer.features.filter((record) => {
    const geometry = record.feature.getGeometry();
    if (!geometry) return false;
    const extent = geometry.getExtent();
    return masterGeometries.some((candidate) => touches(geometry, extent, candidate));
  }).map(({ id }) => id));
}

export interface DisplaySet {
  readonly masterFragmentIds: ReadonlySet<string>;
  readonly layerFeatureIds: ReadonlyMap<string, ReadonlySet<string>>;
}

type DisplaySetInput = Pick<AppState, 'model' | 'workScope' | 'layers'> & Partial<Pick<AppState, 'attributeFilters' | 'operationalFields'>>;
let lastDisplaySet: { readonly input: readonly unknown[]; readonly result: DisplaySet } | null = null;

/**
 * Memoized by input identity: the store is immutable, so the same references always yield the same
 * DisplaySet. This keeps map, table, explorer and status bar on one shared, stable Set instance.
 */
export function deriveDisplaySet(state: DisplaySetInput): DisplaySet {
  const input = [state.model, state.workScope, state.layers, state.attributeFilters, state.operationalFields];
  if (lastDisplaySet && lastDisplaySet.input.every((value, index) => value === input[index])) return lastDisplaySet.result;
  const result = computeDisplaySet(state);
  lastDisplaySet = { input, result };
  return result;
}

function computeDisplaySet(state: DisplaySetInput): DisplaySet {
  const masterFragmentIds = filteredMasterFragmentIds(state.model, state.workScope, state.attributeFilters ?? [], state.operationalFields ?? []);
  const layerFeatureIds = new Map<string, ReadonlySet<string>>();
  state.layers.forEach((layer) => {
    if (layer.kind === 'master') { layerFeatureIds.set(layer.id, masterFragmentIds); return; }
    const scopedIds = featureIdsForLayerScope(layer, state.model, state.workScope);
    layerFeatureIds.set(layer.id, scopedIds);
  });
  return { masterFragmentIds, layerFeatureIds };
}

export const recordsInDisplaySet = (layer: VectorLayerModel, displaySet: DisplaySet): readonly VectorFeatureRecord[] => {
  const ids = displaySet.layerFeatureIds.get(layer.id);
  return ids ? layer.features.filter(({ id }) => ids.has(id)) : layer.features;
};
